import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Paperclip, Save, Send, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import type { Message } from '../../api/types'
import { useI18n } from '../../i18n'

type Draft = { to: string; cc: string; subject: string; body: string }
const draftStorageKey = 'swiss_mail_compose_draft_id'

export function ComposePage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<Draft>({ to: '', cc: '', subject: '', body: '' })
  const [draftID, setDraftID] = useState(() => localStorage.getItem(draftStorageKey) ?? '')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [saveState, setSaveState] = useState('')
  const firstRender = useRef(true)

  const recipients = () => `${draft.to},${draft.cc}`.split(',').map((value) => value.trim()).filter(Boolean)
  const update = (field: keyof Draft, value: string) => setDraft((current) => ({ ...current, [field]: value }))

  async function saveDraft() {
    if (!draft.to && !draft.subject && !draft.body) return
    try {
      const payload = { folder: 'drafts', recipients: recipients(), subject: draft.subject, body: draft.body }
      const result = draftID ? await api.patch<Message>(`/messages/${draftID}`, payload) : await api.post<Message>('/messages', payload)
      if (!draftID) {
        setDraftID(result.id)
        localStorage.setItem(draftStorageKey, result.id)
      }
      setSaveState(t('common.save'))
      setTimeout(() => setSaveState(''), 2000)
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to save draft.') }
  }

  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return }
    const timer = window.setTimeout(() => { void saveDraft() }, 1500)
    return () => window.clearTimeout(timer)
  }, [draft])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const messageRecipients = recipients()
    try {
      setPending(true)
      const payload = { folder: 'sent', recipients: messageRecipients, subject: draft.subject, body: draft.body }
      if (draftID) await api.patch(`/messages/${draftID}`, payload)
      else await api.post('/messages', payload)
      localStorage.removeItem(draftStorageKey)
      navigate('/user/mail/sent')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to send message.') }
    finally { setPending(false) }
  }

  async function discard() {
    try {
      if (draftID) await api.delete(`/messages/${draftID}`)
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to discard draft.'); return }
    localStorage.removeItem(draftStorageKey)
    navigate('/user/mail')
  }

  return (
    <div className="mx-auto max-w-3xl">
      <form onSubmit={submit} className="panel overflow-hidden shadow-lg border-0 ring-1 ring-border/50">
        {/* Header */}
        <header className="relative bg-gradient-to-r from-primary/5 via-primary/3 to-transparent border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Send size={18} />
              </span>
              <div>
                <h1 className="font-semibold text-lg">{t('user.newMessage')}</h1>
                {saveState && <p className="text-xs text-emerald-600 flex items-center gap-1"><Save size={10}/>{saveState}</p>}
              </div>
            </div>
            <button type="button" onClick={() => navigate('/user/mail')} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors">
              <X size={18}/>
            </button>
          </div>
        </header>

        {/* Fields */}
        <div className="px-6 pt-4 space-y-0">
          <label className="flex items-center gap-3 border-b py-3 group">
            <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('user.to')}</span>
            <input value={draft.to} onChange={(e) => update('to', e.target.value)} className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50" type="text" required placeholder="recipient@example.com" />
          </label>
          <label className="flex items-center gap-3 border-b py-3 group">
            <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('user.cc')}</span>
            <input value={draft.cc} onChange={(e) => update('cc', e.target.value)} className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50" type="text" placeholder={t('common.optional')} />
          </label>
          <label className="flex items-center gap-3 border-b py-3 group">
            <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('user.messageSubject')}</span>
            <input value={draft.subject} onChange={(e) => update('subject', e.target.value)} className="min-w-0 flex-1 bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground/50" placeholder={t('user.messageSubject')} />
          </label>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <textarea
            value={draft.body}
            onChange={(e) => update('body', e.target.value)}
            className="min-h-[40vh] sm:min-h-[320px] w-full resize-y rounded-lg border bg-muted/20 p-4 text-sm leading-relaxed outline-none focus:border-primary/30 focus:ring-2 focus:ring-primary/10 transition-shadow placeholder:text-muted-foreground/40"
            placeholder={t('user.messageBody')}
          />
          {error && <p className="mt-3 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}
        </div>

        {/* Footer */}
        <footer className="flex items-center gap-3 border-t bg-muted/10 px-6 py-4">
          <button disabled={pending} className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-lg shadow-sm">
            <Send size={15}/>
            {pending ? t('common.loading') : t('user.send')}
          </button>
          <button type="button" onClick={() => void saveDraft()} aria-label="Save draft" className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <Save size={17}/>
          </button>
          <button type="button" aria-label={t('common.optional')} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <Paperclip size={17}/>
          </button>
          <div className="flex-1" />
          <button type="button" onClick={discard} className="text-sm text-muted-foreground hover:text-destructive transition-colors px-3 py-1.5 rounded-lg hover:bg-destructive/5">
            {t('user.discard')}
          </button>
        </footer>
      </form>
    </div>
  )
}
